// ============================================
// PRTCL (formerly PenRx) App Layout - Bottom Tab Navigation
// with raised center Log Dose button
// ============================================

import React, { useEffect, useState, createContext, useContext, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { startReminderEngine, stopReminderEngine } from '../utils/reminders';
import { startActivationEngine, stopActivationEngine } from '../utils/protocolActivation';
import { isNative, configureStatusBar, onAppUrlOpen, openExternal } from '../utils/native';
import { useKeyboard } from '../utils/useKeyboard';
import { useTheme } from '../design/useTheme';
import { useOnlineStatus } from '../utils/offline';
import { useCloudQuery } from '../data/hooks';
import { fetchActiveProtocols, fetchInventory, fetchProfile } from '../data/cloud';
import { useAuth } from '../auth/AuthProvider';
import { DoseLogModal } from '../features/home/DoseLogModal';
import { AdHocDoseLogModal } from '../features/home/AdHocDoseLogModal';
import { useInventoryAlerts } from '../features/home/useInventoryAlerts';
import { BottomNav } from '../components/BottomNav';
import AnnouncementModal from '../features/announcements/AnnouncementModal';
import './Layout.css';

// Top bar back button context - pages call useTopBack() to place a
// back button in the Layout's top bar, aligned with the right icons.
// Pass a route string for an explicit destination, or -1 to pop the
// browser history stack (useful when entry can come from multiple
// parents, e.g. a peptide reached via Library OR via a Stack).
interface TopBackConfig { label: string; to: string | number }
const TopBackContext = createContext<{
  setTopBack: (config: TopBackConfig | null) => void;
}>({ setTopBack: () => {} });

export function useTopBack(label: string, to: string | number) {
  const { setTopBack } = useContext(TopBackContext);
  useEffect(() => {
    setTopBack({ label, to });
    return () => setTopBack(null);
  }, [label, to, setTopBack]);
}

// Notification tray context - exposed so any page-owned bell (e.g. the
// inline Library bell) can ask Layout to open the existing tray instead
// of duplicating the tray UI.
const NotificationTrayContext = createContext<{ openTray: () => void }>({
  openTray: () => {},
});

export function useNotificationTray() {
  return useContext(NotificationTrayContext);
}

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { isNocturnalPulse, isDark } = useTheme();
  const useLightStatusBar = isNocturnalPulse || isDark;
  const { user } = useAuth();
  useKeyboard();

  // First letter of display_name, else first letter of email, else "?".
  // Kept as synchronous auth-session data - no extra network fetch - since
  // ProfilePage writes display_name back to auth.user_metadata on edit,
  // so this stays in sync with the full profile record.
  const profileInitial = (
    user?.user_metadata?.display_name?.charAt(0) ||
    user?.email?.charAt(0) ||
    '?'
  ).toUpperCase();
  const [showLogModal, setShowLogModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [topBack, setTopBackState] = useState<TopBackConfig | null>(null);
  const setTopBack = useCallback((config: TopBackConfig | null) => setTopBackState(config), []);

  // Inventory alert state lives in a shared hook so any per-page bell
  // (e.g. the inline Library bell) reads from the same source as this
  // global one. Behavior + severity-bracket IDs are unchanged.
  const alerts = useInventoryAlerts();

  const openTray = useCallback(() => {
    alerts.markAllSeen();
    setShowNotifications(true);
  }, [alerts]);

  // Memoize context value objects so Provider consumers don't re-render on
  // every Layout render. Both functions below are already stable via
  // useCallback, so these identities change only when their deps do.
  const topBackContextValue = useMemo(() => ({ setTopBack }), [setTopBack]);
  const notificationTrayContextValue = useMemo(() => ({ openTray }), [openTray]);

  // activeProtocols is still needed here for the DoseLogModal that Layout owns.
  const { data: activeProtocols } = useCloudQuery(
    'protocols',
    fetchActiveProtocols,
    [],
  );

  // 2026-04-29: Layout also owns the global ad-hoc dose log path. When the
  // user has zero active protocols but has in-use vials, the FAB "Log
  // dose" button routes through AdHocDoseLogModal instead of the
  // protocol-driven one (which would render an empty picker). Inventory
  // is fetched at the Layout level so the modal has the vial list ready
  // when the FAB is tapped.
  const { data: inventoryAll } = useCloudQuery('inventory', fetchInventory, []);

  // Profile read (cache-warm from HomePage) so the top bar can tell when
  // the dashboard is in its brand-new EmptyDashboard state - same
  // condition HomePage uses to render it.
  const { data: topBarProfile } = useCloudQuery('user_profiles', fetchProfile, null);
  const inUseVials = useMemo(
    () => inventoryAll.filter((v) => v.isActive && !v.isResearching),
    [inventoryAll]
  );
  const useAdHocLog = activeProtocols.length === 0 && inUseVials.length > 0;

  // Start reminder check engine on app load
  useEffect(() => {
    startReminderEngine();
    return () => stopReminderEngine();
  }, []);

  // Scheduled-protocol activation engine: reconciles scheduled -> active on
  // boot, app foreground, and protocol changes (docs/2026-07-16-scheduled-
  // activation-phase4-plan.md).
  useEffect(() => {
    startActivationEngine();
    return () => stopActivationEngine();
  }, []);

  // Configure native status bar - re-fires on theme change so dark surfaces
  // (nocturnal-pulse or dark mode) get light (white) text and light themes
  // get dark text.
  useEffect(() => {
    if (isNative()) {
      configureStatusBar(useLightStatusBar);
    }
  }, [useLightStatusBar]);

  // Handle deep link opens (universal links). Preserve query + hash:
  // pathname-only navigation dropped ?ref=<share> (FAQ-share attribution
  // never fired on native) and would strip any hash-carried auth tokens.
  useEffect(() => {
    onAppUrlOpen((url) => {
      try {
        const u = new URL(url);
        if (u.pathname) navigate(u.pathname + u.search + u.hash);
      } catch {
        // Invalid URL, ignore
      }
    });
  }, [navigate]);

  // Intercept clicks on <a target="_blank"> and route through Safari View
  // Controller (via @capacitor/browser) so users don't get trapped in the
  // WKWebView. Apple Guideline 4.2 flags this pattern; also improves UX
  // (back-to-app button, session preservation).
  // Same-origin in-app routes are left alone.
  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a') as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target !== '_blank') return;
      const href = anchor.getAttribute('href') ?? '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      // Same-origin (/terms, /privacy) - let react-router handle it instead.
      if (href.startsWith('/')) {
        ev.preventDefault();
        navigate(href);
        return;
      }
      ev.preventDefault();
      openExternal(href);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [navigate]);

  // Don't show tab bar during onboarding, walkthrough, auth, or landing pages
  const isOnboarding = location.pathname.startsWith('/onboarding');
  const isWalkthrough = location.pathname === '/calculator/walkthrough' || location.pathname === '/beta-onboarding' || location.pathname === '/welcome';
  // The Add Protocol wizard is a full-screen guided flow with its own header
  // and Close button. Hide the tab bar + blur backdrops so its flat teal
  // surface fills the screen (otherwise the nav's white blur bleeds in).
  const isWizard = location.pathname === '/protocols/new';
  const isAuthPage = ['/signin', '/signup', '/forgot-password', '/reset-password', '/beta'].includes(location.pathname);
  const isLandingPage = location.pathname === '/' || location.pathname === '/v2' || location.pathname === '/terms' || location.pathname === '/privacy' || location.pathname === '/consumer-health-data' || location.pathname.startsWith('/share/') || location.pathname === '/learn' || location.pathname === '/your-first-peptide' || location.pathname === '/reconstitution' || location.pathname.startsWith('/tools/');
  const isGatePage = location.pathname === '/disclaimer' || location.pathname === '/subscribe';
  const showNav = !isOnboarding && !isWalkthrough && !isWizard && !isAuthPage && !isLandingPage && !isGatePage;
  // Top icons (bell + profile chip) only render on the home dashboard.
  // Every other route either owns its own page chrome (Library, Detail,
  // Calculator, Protocols, Profile) or doesn't need these affordances
  // (auth, onboarding, landing). Bell still gates internally on
  // visibleAlerts.length > 0.
  const isDashboardPage = location.pathname === '/dashboard';
  const showTopIcons = showNav && isDashboardPage;

  // Auto-hide the TOP ICONS (bell + profile) on scroll down so they
  // don't sit over scrolling content on mobile. The blur backdrop stays
  // visible always - only the pill icons translate out. The CSS rule
  // for .layout-top-hidden is gated to mobile widths, so on desktop
  // the class is applied but has no visual effect (icons stay pinned).
  const [topHidden, setTopHidden] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    // Track cumulative direction so slow scrolls still trigger - the old
    // per-frame delta check needed ~8px/frame which never happens on a
    // slow finger drag. Instead, track accumulated movement in the
    // current direction and flip state when it passes a small threshold.
    let accum = 0;
    const triggerDistance = 6;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        ticking = false;
        // Always show when near the top, regardless of direction
        if (y < 40) {
          setTopHidden(false);
          accum = 0;
          return;
        }
        // Reset accumulator on direction change so the next flip is
        // responsive rather than needing to cancel out old movement.
        if ((delta > 0 && accum < 0) || (delta < 0 && accum > 0)) accum = 0;
        accum += delta;
        if (accum > triggerDistance) {
          setTopHidden(true);
          accum = 0;
        } else if (accum < -triggerDistance) {
          setTopHidden(false);
          accum = 0;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);
  useEffect(() => { setTopHidden(false); }, [location.pathname]);

  return (
    <>
      {/* Top blur backdrop - fades content under status bar + icon row.
          Always visible, never auto-hides. */}
      {showNav && <div className="layout-top-blur" />}

      {/* Top bar - back button (left) + notification/profile (right).
          Auto-hides on scroll-down on mobile (class below is mobile-
          scoped in CSS); on desktop the class has no visual effect. */}
      {showTopIcons && (
        <div className={`layout-top-bar${topHidden ? ' layout-top-hidden' : ''}`}>
          <div className="layout-top-left">
            {topBack && (
              <button className="layout-back-btn" onClick={() => navigate(topBack.to as never)} aria-label={`Back to ${topBack.label}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                {topBack.label}
              </button>
            )}
          </div>
        <div className="layout-top-actions">
          {/* Notification bell - renders when the tray would have content.
              Gated on visibleAlerts (alerts minus dismissed) so the bell and
              tray stay in sync: if you dismissed everything, the bell hides
              too, and you never open an empty panel. The severity-bracket
              alert IDs (see alerts useMemo above) mean dismissal only silences
              the current bracket - if a condition worsens (low→empty,
              soon→urgent→expired), a new ID surfaces and the bell returns. */}
          {alerts.visibleAlerts.length > 0 && (
            <div className="layout-bell-wrapper">
              <button
                className="layout-bell-btn"
                onClick={() => {
                  if (!showNotifications) {
                    alerts.markAllSeen();
                  }
                  setShowNotifications(!showNotifications);
                }}
                aria-label="Notifications"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {alerts.alertCount > 0 ? (
                  <span className="layout-bell-badge">{alerts.alertCount}</span>
                ) : (
                  <span className="layout-bell-dot" aria-hidden="true" />
                )}
              </button>
            </div>
          )}

          {/* Resources button (lightbulb) - 2026-05-15 lift from the
              bottom dashboard card into the top bar. Same visibility as
              the profile + bell icons (gated by showTopIcons). Circular
              chip with teal stroke (Variant B per Lawrence's pick).
              Reads as "tap for tips / FAQs / walkthroughs."
              Hidden on the brand-new EmptyDashboard state (2026-07-11,
              Lawrence): the welcome banner + two icons crowds the top
              bar, and that screen already leads with guide entries. */}
          {!(isDashboardPage && topBarProfile && !topBarProfile.hasCreatedFirstProtocol && activeProtocols.length === 0) && (
            <button
              className="layout-resources-btn"
              onClick={() => navigate('/resources')}
              aria-label="Helpful Resources"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6" />
                <path d="M10 22h4" />
                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
              </svg>
            </button>
          )}

          {/* Profile avatar - initial on a circular card chip, mirroring
              the prof-hero-avatar style but adapted for the light top bar. */}
          <button
            className="layout-profile-btn"
            onClick={() => navigate('/profile')}
            aria-label="Profile"
          >
            {profileInitial}
          </button>
        </div>
        </div>
      )}

      {/* Notification tray - full-width bar below top actions */}
      {showNotifications && (
        <div className="layout-notif-tray" onClick={() => setShowNotifications(false)}>
          <div className="layout-notif-tray-content" onClick={(e) => e.stopPropagation()}>
            {alerts.visibleAlerts.length > 0 ? (
              alerts.visibleAlerts.map((alert) => (
                <div key={alert.id} className={`layout-notif-item layout-notif-${alert.severity}`}>
                  <button
                    className="layout-notif-item-main"
                    onClick={() => { setShowNotifications(false); navigate('/protocols?tab=supply'); }}
                  >
                    <span className={`layout-notif-icon ${alert.severity === 'critical' ? 'layout-notif-danger' : alert.severity === 'warn' ? 'layout-notif-warn' : 'layout-notif-info'}`}>
                      {alert.severity === 'critical' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      )}
                    </span>
                    <div className="layout-notif-text">
                      <span className="layout-notif-label">{alert.label}</span>
                      <span className="layout-notif-detail">{alert.detail}</span>
                    </div>
                  </button>
                  <button
                    className="layout-notif-dismiss"
                    onClick={(e) => {
                      e.stopPropagation();
                      alerts.dismiss(alert.id);
                    }}
                    aria-label="Dismiss"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))
            ) : (
              <div className="layout-notif-empty">
                <span>All clear - your vials are stocked and fresh.</span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* layout--bell-visible lets page CSS (e.g. ed-page-title) reserve
          headroom for the third fixed top-right icon when the alert
          bell is rendered. */}
      <div className={`layout${topBack ? ' layout--has-back' : ''}${alerts.visibleAlerts.length > 0 ? ' layout--bell-visible' : ''}`}>
      {!isOnline && (
        <div style={{
          background: 'var(--color-warning-bg)',
          color: 'var(--color-warning)',
          padding: '8px 16px',
          fontSize: 'var(--font-size-xs)',
          textAlign: 'center',
          fontWeight: 500,
        }}>
          You're offline. Some features may be unavailable.
        </div>
      )}
      <main className="layout-main">
        <NotificationTrayContext.Provider value={notificationTrayContextValue}>
          <TopBackContext.Provider value={topBackContextValue}>
            {children}
          </TopBackContext.Provider>
        </NotificationTrayContext.Provider>
      </main>
      {showNav && <div className="layout-bottom-blur" />}
      {showNav && <BottomNav onLogDose={() => setShowLogModal(true)} />}

      {/* Global dose log modal. We render exactly ONE of these at a time
          based on whether the user is in protocol-mode or ad-hoc-mode.
          Mounting both would cause two modals to open from one FAB tap;
          rendering just the relevant one keeps the open/close semantics
          clean. The fallback (no protocols + no in-use vials) still shows
          the protocol-driven modal so the empty state is consistent — the
          AdHocDoseLogModal's own "add a vial first" path also handles
          this, but ad-hoc mode is gated above on inUseVials.length > 0
          to avoid flashing the wrong empty state. */}
      {useAdHocLog ? (
        <AdHocDoseLogModal
          isOpen={showLogModal}
          onClose={() => setShowLogModal(false)}
          vial={inUseVials.length === 1 ? inUseVials[0] : null}
          vials={inUseVials.length > 1 ? inUseVials : undefined}
        />
      ) : (
        <DoseLogModal
          isOpen={showLogModal}
          onClose={() => setShowLogModal(false)}
          protocol={activeProtocols.length === 1 ? activeProtocols[0] : null}
          protocols={activeProtocols}
        />
      )}

      {/* "What's new" announcement overlay. Only renders inside the
          authenticated app chrome — onboarding / auth / welcome / legal
          surfaces stay clean. Internally gated on a version-greater-than-
          seen comparison so each row only fires once per device. */}
      {user && showNav && <AnnouncementModal />}
    </div>
    </>
  );
};
